import { EntityEvent, IEntity } from './entity.js'
import {
  applyDiagramPatch,
  createEmptyDiagramDocument,
  isDiagramDocument
} from './diagram.js'
import type { DiagramDocument, DiagramPatchOperation } from './diagram.js'
import { ErrorEventInstanceObj, EventInstanceObj } from './objects.js'
import { Prettify } from './types.js'

export interface FieldDiff {
  field: string
  oldValue: any
  newValue: any
}

export interface UpdateMeta {
  editorId?: string
  [key: string]: unknown
}

export class EntityUpdateContext {
  diff: FieldDiff[]
  meta?: UpdateMeta

  constructor(data: any) {
    if (Array.isArray(data)) {
      this.diff = data
    } else if (data && typeof data === 'object' && 'diff' in data) {
      this.diff = data.diff
      if (data.meta) this.meta = data.meta
    } else {
      this.diff = []
    }
  }

  static create(
    oldEntity: IEntity,
    newData: Record<string, any>,
    meta?: UpdateMeta
  ): EntityUpdateContext {
    const diff = EntityUpdateContext.createDiff(oldEntity, newData)
    const ctx = new EntityUpdateContext(diff)
    if (meta) ctx.meta = meta
    return ctx
  }

  private static createDiff(
    oldEntity: IEntity,
    newData: Record<string, any>
  ): FieldDiff[] {
    const diffs: FieldDiff[] = []

    // Only iterate over keys in newData (the values that are being changed)
    for (const key in newData) {
      if (Object.prototype.hasOwnProperty.call(newData, key)) {
        const oldValue = (oldEntity as any)[key]
        const newValue = newData[key]

        // Only include fields that have actually changed
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          diffs.push({
            field: key,
            oldValue: oldValue,
            newValue: newValue
          })
        }
      }
    }

    return diffs
  }

  /**
   * Apply the update context to an entity by updating its fields with the new values
   * @param entity - The entity to apply the updates to
   */
  applyDiffTo(entity: IEntity): void {
    for (const d of this.diff) {
      ;(entity as any)[d.field] = d.newValue
    }
  }
}

export interface DiagramUpdateMeta extends UpdateMeta {
  interaction?: 'drag' | 'resize' | 'connect' | 'label-edit' | 'viewport'
}

export interface IDiagramUpdateContext {
  kind: 'diagram'
  patch: DiagramPatchOperation[]
  meta?: DiagramUpdateMeta
}

export class DiagramUpdateContext
  extends EntityUpdateContext
  implements IDiagramUpdateContext
{
  readonly kind = 'diagram' as const
  patch: DiagramPatchOperation[]

  constructor(data?: DiagramPatchOperation[] | IDiagramUpdateContext) {
    super([])
    if (Array.isArray(data)) {
      this.patch = data
    } else if (data && typeof data === 'object') {
      this.patch = Array.isArray(data.patch) ? data.patch : []
      if (data.meta) this.meta = data.meta
    } else {
      this.patch = []
    }
  }

  static fromPatch(
    patch: DiagramPatchOperation[],
    meta?: DiagramUpdateMeta
  ): DiagramUpdateContext {
    return new DiagramUpdateContext(
      meta
        ? {
            kind: 'diagram',
            patch,
            meta
          }
        : {
            kind: 'diagram',
            patch
          }
    )
  }

  override applyDiffTo(entity: IEntity): void {
    if (entity.entityType !== 'obj') {
      return
    }

    const currentData = isDiagramDocument(entity.data)
      ? entity.data
      : createEmptyDiagramDocument()
    ;(entity as unknown as { data?: DiagramDocument }).data = applyDiagramPatch(
      currentData,
      this.patch
    )
  }
}

export function isDiagramUpdateContext(
  context: unknown
): context is DiagramUpdateContext | IDiagramUpdateContext {
  return (
    !!context &&
    typeof context === 'object' &&
    'kind' in context &&
    context.kind === 'diagram' &&
    'patch' in context &&
    Array.isArray(context.patch)
  )
}

export type IFunctionStartContext = Prettify<{
  rootObjectId?: string
  entities?: IEntity[]
  data?: Record<string, unknown>
  inIds?: string[]
  outIds?: string[]
}>

export class FunctionStartContext implements IFunctionStartContext {
  rootObjectId?: string
  entities?: IEntity[]
  data?: Record<string, unknown>
  inIds?: string[]
  outIds?: string[]

  functionInputMapping?: { [key: string]: string[] }
  inEventInstances!: EventInstanceObj[]

  public constructor(contextOpts: IFunctionStartContext) {
    Object.assign(this, contextOpts)
    if (contextOpts.rootObjectId !== undefined) {
      this.rootObjectId = contextOpts.rootObjectId
    }
    this.data = contextOpts.data || {}
    this.entities = contextOpts.entities || []
    this.inEventInstances = this.entities.filter(
      (obj) => this.inIds?.includes(obj.id) && obj.typeOf === 'Event_Instance'
    ) as EventInstanceObj[]
  }

  /**
   * Gets the root object that owns this context
   * @returns The root object or undefined if not found
   */
  getRootObject() {
    return this.entities?.find((obj) => obj.id === this.rootObjectId)
  }

  /**
   * Gets an environment variable from the root object
   * @param key - The environment variable key
   * @returns The environment variable value or undefined if not found
   */
  // getEnvironmentVariable(key: string): string | undefined {
  //   const rootObj = this.getRootObject()
  //   return rootObj?.environment?.[key]
  // }

  /**
   * Gets a secret from the root object
   * @param key - The secret key
   * @returns The secret value or undefined if not found
   */
  // getSecret(key: string): string | undefined {
  //   const rootObj = this.getRootObject()
  //   return rootObj?.secrets?.[key]
  // }
}

export interface IFunctionStopContext<T extends object = any> {
  data?: T
  err?: Error
  entityEvents?: EntityEvent[]
  events?: EventInstanceObj[]
  errEvents?: ErrorEventInstanceObj[]
}

export class FunctionStopContext<
  T extends object = any
> implements IFunctionStopContext<T> {
  data?: T
  err?: Error
  entityEvents?: EntityEvent[]
  events?: EventInstanceObj[]
  errEvents?: ErrorEventInstanceObj[]

  public constructor(contextOpts?: IFunctionStopContext<T>) {
    if (contextOpts?.data !== undefined) {
      this.data = contextOpts.data
    }
    if (contextOpts?.err !== undefined) {
      this.err = contextOpts.err
    }
    if (contextOpts?.entityEvents !== undefined) {
      this.entityEvents = contextOpts.entityEvents
    }
    if (contextOpts?.events !== undefined) {
      this.events = contextOpts.events
    }
    if (contextOpts?.errEvents !== undefined) {
      this.errEvents = contextOpts.errEvents
    }
  }
}
